/**
 * useJourneyStore — client-issued user-day identifier with a 24 h sliding TTL.
 *
 * App-scoped Pinia singleton. The journey id is a UUID generated on first
 * interaction and stored in localStorage under `speedtest-journey`. Every
 * API request calls `touch()` which refreshes the
 * `speedtest-journey-touchedAt` timestamp. If a stored UUID exists and was
 * touched within 24 h it is reused; otherwise a fresh UUID is generated.
 *
 * If localStorage is unavailable (private modes, embedded contexts) the store
 * falls back to an in-memory id without throwing.
 */

import { defineStore } from "pinia";
import { computed, ref, type ComputedRef, type Ref } from "vue";

const JOURNEY_KEY = "speedtest-journey";
const JOURNEY_TOUCHED_KEY = "speedtest-journey-touchedAt";
const TTL_MS = 24 * 60 * 60 * 1000;

function hasStorage(): boolean {
    try {
        return typeof localStorage !== "undefined";
    } catch {
        return false;
    }
}

function readStored(): { id: string; touchedAt: number } | null {
    if (!hasStorage()) return null;
    try {
        const id = localStorage.getItem(JOURNEY_KEY);
        const touchedAtRaw = localStorage.getItem(JOURNEY_TOUCHED_KEY);
        if (!id || !touchedAtRaw) return null;
        const touchedAt = Number.parseInt(touchedAtRaw, 10);
        if (!Number.isFinite(touchedAt)) return null;
        return { id, touchedAt };
    } catch {
        return null;
    }
}

function writeStored(id: string, touchedAt: number): void {
    if (!hasStorage()) return;
    try {
        localStorage.setItem(JOURNEY_KEY, id);
        localStorage.setItem(JOURNEY_TOUCHED_KEY, String(touchedAt));
    } catch {
        // Quota exceeded or storage disabled — fall through; in-memory id stands.
    }
}

function generateId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    // Last-resort fallback for ancient environments. Not cryptographically
    // strong but the journey id is not an authority — `sessionId` is.
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

export const useJourneyStore = defineStore("journey", () => {
    const now = Date.now();
    const stored = readStored();

    let initialId: string;
    if (stored && now - stored.touchedAt < TTL_MS) {
        initialId = stored.id;
    } else {
        initialId = generateId();
        writeStored(initialId, now);
    }

    const idRef: Ref<string> = ref(initialId);

    function touch(): void {
        const tNow = Date.now();
        // Re-read each touch in case a sibling tab generated a new id.
        const cur = readStored();
        if (cur && tNow - cur.touchedAt < TTL_MS) {
            idRef.value = cur.id;
            writeStored(cur.id, tNow);
            return;
        }
        // Stored entry missing or expired — regenerate.
        const fresh = generateId();
        idRef.value = fresh;
        writeStored(fresh, tNow);
    }

    const journeyId: ComputedRef<string> = computed(() => idRef.value);

    // ── Session-scope auto-start latch ─────────────────────────────
    //
    // The latch lives on the journey store (not on `useAutoStart`'s
    // local state) so it survives a `TraditionalSpeedtest` remount.
    // Mount-scoped latches would re-arm on back-nav and fire a second
    // test the user never asked for.
    //
    // Reset path: `onDockRetake` (the explicit user-initiated retake) +
    // `onDockStart` (the explicit Start click) clear the latch via
    // `clearAutoStartLatch()` so the next mount re-arms. Back-nav from
    // the survey does NOT clear it.
    const hasAutoStartedThisSession: Ref<boolean> = ref(false);

    function markAutoStarted(): void {
        hasAutoStartedThisSession.value = true;
    }

    function clearAutoStartLatch(): void {
        hasAutoStartedThisSession.value = false;
    }

    return {
        journeyId,
        touch,
        hasAutoStartedThisSession,
        markAutoStarted,
        clearAutoStartLatch,
    };
});
